/* eslint-disable react/forbid-prop-types,no-param-reassign */
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState, useCallback } from "react";
import { Box, Collapse, Grid, Typography, Select, MenuItem, FormControl } from "@mui/material";
import _ from "lodash";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";

import SecondaryButton from "./SecondaryButton";
import Form from "../form/Form";

const Filter = ({ filterSteps, filterSchema, items, setFilteredItems, shouldStartOpen, hideInternalButton }) => {
  const [hideFilters, setHideFilters] = useState(shouldStartOpen);
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);

  const form = useForm({
    resolver: yupResolver(filterSchema),
    mode: "onChange",
  });

  const flatten = (obj, prefix = "", res = {}) =>
    Object.entries(obj).reduce((r, [key, val]) => {
      const k = prefix ? `${prefix}.${key}` : key;
      if (typeof val === "object" && val !== null && !Array.isArray(val)) {
        flatten(val, k, r);
      } else if (val !== null && val !== undefined && val !== "") {
        r[k] = val;
      }
      return r;
    }, res);

  const handleSubmit = useCallback(async () => {
    await form.trigger();
    if (Object.keys(form.formState.errors).length === 0) {
      const filterObject = flatten(form.getValues());

      const keys = Object.keys(filterObject).filter((k) => k !== "tab" && filterObject[k] !== undefined);
      if (keys.length > 0) {
        setFilteredItems(
          items.filter((i) => keys.every((key) => {
              const searchableField = filterSteps.flatMap((step) => step.fields).find((field) => field.name === key && field.searchableFieldName)?.searchableFieldName;

              const valueToCheck = _.get(i, searchableField || key);
              const filterValue = filterObject[key];

              if (valueToCheck === undefined || valueToCheck === null) return false;

              if (Array.isArray(valueToCheck)) {
                if (typeof filterValue === "string") {
                  return valueToCheck.some((el) => el && el.toString().toLowerCase().includes(filterValue.toLowerCase()));
                } if (Array.isArray(filterValue)) {
                  return filterValue.every((v) => valueToCheck.includes(v));
                }
              }

              return valueToCheck.toString().toLowerCase().includes(filterValue.toString().toLowerCase());
            })),
        );
      } else {
        setFilteredItems(items);
      }
    }
  }, [form, items, filterSteps, setFilteredItems]);

  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries());
    Object.keys(params).forEach((key) => form.setValue(key, params[key]));
    handleSubmit();
  }, [items, searchParams, form, handleSubmit]);

  useEffect(() => {
    const subscription = form.watch(() => {
      const timer = setTimeout(() => handleSubmit(), 300);
      return () => clearTimeout(timer);
    });
    return () => subscription.unsubscribe();
  }, [form, handleSubmit]);

  const toggleFilters = () => setHideFilters((val) => !val);

  useEffect(() => {
    if (hideInternalButton) {
      window.toggleFilters = toggleFilters;
    }
  }, [hideInternalButton]);

  return (
    <Box sx={{ marginBottom: "20px" }}>
      {!hideInternalButton && (
        <Box textAlign="right" marginBottom="20px">
          <SecondaryButton onClick={toggleFilters}>{hideFilters ? "Hide Filters" : "Show Filters"}</SecondaryButton>
        </Box>
      )}
      <Collapse orientation="vertical" in={hideFilters}>
        <Form
          steps={filterSteps}
          form={form}
          onSubmit={handleSubmit}
          startPage={currentStep}
          FormButtons={
            <Grid container sx={{ width: "40%", margin: "auto", display: "flex", justifyContent: "center" }}>
              <Grid item xs={12} sm={12} md={6} lg={6} sx={{ display: "flex", justifyContent: "center" }}>
                <Typography
                  variant="h6"
                  sx={{ fontSize: "15px", fontWeight: "400", cursor: "pointer", textAlign: "center", width: "100%" }}
                  onClick={async () => {
                    await form.reset();
                    setFilteredItems(items);
                  }}>
                  Reset all filters
                </Typography>
              </Grid>
            </Grid>
          }
          options={{
            showTitle: true,
            sameLine: true,
          }}
        />
        <Box sx={{ display: "flex", justifyContent: "center", gap: "10px", mt: 2, mx: { xs: 2, md: 0 } }}>
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: "10px" }}>
            {filterSteps.map((step, index) => (
              <SecondaryButton
                key={step.title}
                onClick={() => setCurrentStep(index)}
                sx={{
                  backgroundColor: currentStep === index ? "#C02026" : "transparent",
                  color: currentStep === index ? "white" : "inherit",
                }}>
                {step.title}
              </SecondaryButton>
            ))}
          </Box>

          <Box sx={{ display: { xs: "block", md: "none" }, minWidth: "200px" }}>
            <FormControl fullWidth size="small">
              <Select
                value={currentStep}
                onChange={(e) => setCurrentStep(e.target.value)}
                displayEmpty
                sx={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#C02026",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#C02026",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#C02026",
                  },
                }}>
                {filterSteps.map((step, index) => (
                  <MenuItem key={step.title} value={index}>
                    {step.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

Filter.propTypes = {
  items: PropTypes.array,
  setFilteredItems: PropTypes.func.isRequired,
  filterSchema: PropTypes.object.isRequired,
  filterSteps: PropTypes.array.isRequired,
  shouldStartOpen: PropTypes.bool,
  hideInternalButton: PropTypes.bool,
};

Filter.defaultProps = {
  items: [],
  shouldStartOpen: false,
  hideInternalButton: false,
};

export default Filter;
